<template>
  <div
    class="softphone-widget d-flex flex-column"
    data-cy="softphone-widget"
  >
    <div class="softphone-widget__status text-center">
      <span
        class="softphone-widget__badge"
        :class="statusClass"
        data-cy="softphone-status"
      >
        {{ softphoneStatusLabel }}
      </span>
    </div>

    <div class="text-center mt-3">
      <div class="text-muted small text-uppercase">
        Campaign
      </div>

      <div
        class="softphone-widget__campaign"
        data-cy="softphone-campaign"
      >
        {{ campaignName || '—' }}
      </div>
    </div>

    <!--
      The whole point of the widget window: an agent working in another tab, or another application
      entirely, still gets told a call is here. The banner and the ringtone are the notification;
      the title flash is what reaches them when the window is behind something else.
    -->
    <div
      v-if="isRinging"
      class="softphone-widget__incoming mt-3 text-center"
      data-cy="softphone-incoming"
    >
      <font-awesome-icon
        :icon="['fa', 'phone-volume']"
        class="softphone-widget__incoming-icon"
      />

      <div class="softphone-widget__incoming-label mt-1">
        Incoming call
      </div>
    </div>

    <div
      v-else-if="isCallActive"
      class="text-center"
    >
      <div
        class="softphone-widget__timer mt-3"
        data-cy="softphone-call-timer"
      >
        {{ elapsedTime }}
      </div>

      <!--
        Masked until the call reaches the campaign's minimum duration - the point where the call
        becomes billable - and shown in full from the start when the campaign has no minimum. The
        last four digits are enough to confirm who is on the line until then.
      -->
      <div
        v-if="callerId"
        class="softphone-widget__caller-id mt-1"
        :class="{ 'softphone-widget__caller-id--masked': isCallerIdMasked }"
        data-cy="softphone-caller-id"
      >
        {{ callerId }}
      </div>
    </div>

    <div
      v-else-if="isWrappingUp"
      class="softphone-widget__wrap-up mt-3 text-center text-muted"
      data-cy="softphone-wrap-up"
    >
      Wrap-up · campaign paused · {{ wrapUpSecondsRemaining }}s
    </div>

    <div
      v-if="lastError"
      class="softphone-widget__error mt-3 small text-danger text-center"
      data-cy="softphone-error"
    >
      {{ lastError }}
    </div>

    <div class="mt-auto pt-3">
      <!--
        Plain b-button rather than BaseButtonLoader: that component wraps its button in a
        b-form-group, so the disabled state and the data-cy would land on a fieldset instead of the
        control. These two buttons ARE the softphone - what they are doing has to be unambiguous.
      -->
      <div class="d-flex">
        <b-button
          class="flex-fill mr-2"
          type="button"
          variant="success"
          :disabled="!canPickUp"
          data-cy="softphone-pick-up"
          @click="onPickUp"
        >
          <b-spinner
            v-if="isAnswering"
            small
          />
          <span v-else>Pick up</span>
        </b-button>

        <b-button
          class="flex-fill"
          type="button"
          variant="danger"
          :disabled="!canHangUp"
          data-cy="softphone-hang-up"
          @click="onHangUp"
        >
          <b-spinner
            v-if="isHangingUp"
            small
          />
          <span v-else>Hang up</span>
        </b-button>
      </div>

      <div class="d-flex mt-2">
        <b-button
          class="flex-fill mr-2"
          type="button"
          size="sm"
          variant="outline-secondary"
          :disabled="!isRinging"
          data-cy="softphone-decline"
          @click="onDecline"
        >
          Decline
        </b-button>

        <b-button
          class="flex-fill"
          type="button"
          size="sm"
          variant="outline-secondary"
          :disabled="!isCallActive"
          data-cy="softphone-mute"
          @click="onToggleMute"
        >
          <font-awesome-icon
            :icon="['fa', isMuted ? 'microphone-slash' : 'microphone']"
            class="mr-1"
          />
          {{ isMuted ? 'Unmute' : 'Mute' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import { showErrorMessage } from '@/notification-utils'
import { formatCallerId, maskCallerId } from '@/services/softphone/callerId'
import { armRingtone, isRingtoneArmed } from '@/services/softphone/ringtone'
import { SOFTPHONE_STATUS } from '@/store/modules/softphone.store'

const BASE_DOCUMENT_TITLE = 'Softphone'

export default {
  name: 'SoftphoneWidget',

  data() {
    return {
      elapsedSeconds: 0,
      wrapUpSecondsRemaining: 0,
      isAnswering: false,
      isHangingUp: false,
      tickIntervalId: null,
      titleFlashIntervalId: null
    }
  },

  computed: {
    ...mapState('softphone', [
      'activeCall',
      'incomingCall',
      'isMuted',
      'lastError',
      'status'
    ]),

    ...mapGetters('softphone', [
      'callerIdRevealAfterSeconds',
      'isRinging',
      'isCallActive',
      'liveCampaignName',
      'softphoneStatusLabel'
    ]),

    campaignName() {
      return this.incomingCall?.campaignName || this.activeCall?.campaignName || this.liveCampaignName
    },

    isWrappingUp() {
      return this.status === SOFTPHONE_STATUS.WRAP_UP
    },

    /**
     * Only ever shown on an ANSWERED call - a ringing call deliberately shows nothing but the
     * campaign, so a number never sits on screen for a call nobody has taken.
     */
    callerId() {
      const callerNumber = this.activeCall?.callerNumber

      if (!this.isCallActive || !callerNumber) {
        return ''
      }

      return this.isCallerIdMasked ? maskCallerId(callerNumber) : formatCallerId(callerNumber)
    },

    // Masked until the campaign's minimum duration has elapsed; a campaign without one reveals the
    // number immediately, because `callerIdRevealAfterSeconds` is then 0.
    isCallerIdMasked() {
      return this.elapsedSeconds < this.callerIdRevealAfterSeconds
    },

    // Exactly the spec: pick up is live only while a call is ringing, hang up only while one is up,
    // and after the call ends neither is.
    canPickUp() {
      return this.isRinging && !this.isAnswering
    },

    canHangUp() {
      return this.isCallActive && !this.isHangingUp
    },

    statusClass() {
      return {
        'softphone-widget__badge--ringing': this.isRinging,
        'softphone-widget__badge--active': this.isCallActive,
        'softphone-widget__badge--error': this.status === SOFTPHONE_STATUS.ERROR,
        'softphone-widget__badge--offline': this.status === SOFTPHONE_STATUS.OFFLINE
      }
    },

    elapsedTime() {
      const minutes = Math.floor(this.elapsedSeconds / 60)
      const seconds = this.elapsedSeconds % 60

      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },

  watch: {
    isCallActive(isActive) {
      if (isActive) {
        this.startTimer()
      } else {
        this.stopTimer()
      }
    },

    isRinging(isRinging) {
      if (isRinging) {
        this.startTitleFlash()
      } else {
        this.stopTitleFlash()
      }
    },

    isWrappingUp(isWrappingUp) {
      if (isWrappingUp) {
        this.startTimer()
      } else {
        this.stopTimer()
      }
    }
  },

  created() {
    this.becomeOwner()
  },

  async mounted() {
    document.title = BASE_DOCUMENT_TITLE

    // A window opened from a click usually carries the gesture, but not always, and a ringtone that
    // never plays is the one failure an agent cannot see coming. Try now, and hook the first
    // interaction with the window as a second chance.
    const isArmed = await armRingtone()

    if (!isArmed) {
      this.armOnFirstInteraction()
    }

    window.addEventListener('beforeunload', this.onBeforeUnload)
  },

  beforeDestroy() {
    this.stopTimer()
    this.stopTitleFlash()
    this.removeArmListeners()

    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },

  methods: {
    ...mapActions('softphone', [
      'becomeOwner',
      'answerCall',
      'declineCall',
      'hangUp',
      'setMuted',
      'teardownSoftphone'
    ]),

    armOnFirstInteraction() {
      this.retryArm = async () => {
        await armRingtone()

        if (isRingtoneArmed()) {
          this.removeArmListeners()
        }
      }

      window.addEventListener('pointerdown', this.retryArm)
      window.addEventListener('keydown', this.retryArm)
    },

    removeArmListeners() {
      if (!this.retryArm) {
        return
      }

      window.removeEventListener('pointerdown', this.retryArm)
      window.removeEventListener('keydown', this.retryArm)

      this.retryArm = null
    },

    /**
     * Closing the window means going offline, so the campaign must be paused. `beforeunload` cannot
     * await, so this fires the teardown and lets the keepalive request outlive the page - see
     * `pauseLiveCampaign`.
     */
    onBeforeUnload() {
      this.teardownSoftphone({ isUnloading: true })
    },

    startTimer() {
      this.stopTimer()

      this.tickIntervalId = setInterval(() => {
        const answeredAt = this.activeCall?.answeredAt

        this.elapsedSeconds = answeredAt
          ? Math.max(0, Math.floor((Date.now() - answeredAt) / 1000))
          : 0

        const wrapUpEndsAt = this.$store.state.softphone.wrapUpEndsAt

        this.wrapUpSecondsRemaining = wrapUpEndsAt
          ? Math.max(0, Math.ceil((wrapUpEndsAt - Date.now()) / 1000))
          : 0
      }, 1000)
    },

    stopTimer() {
      if (this.tickIntervalId) {
        clearInterval(this.tickIntervalId)
        this.tickIntervalId = null
      }

      this.elapsedSeconds = 0
      this.wrapUpSecondsRemaining = 0
    },

    startTitleFlash() {
      this.stopTitleFlash()

      let isAlternate = false

      this.titleFlashIntervalId = setInterval(() => {
        isAlternate = !isAlternate
        document.title = isAlternate ? '☎ Incoming call' : BASE_DOCUMENT_TITLE
      }, 1000)
    },

    stopTitleFlash() {
      if (this.titleFlashIntervalId) {
        clearInterval(this.titleFlashIntervalId)
        this.titleFlashIntervalId = null
      }

      document.title = BASE_DOCUMENT_TITLE
    },

    async onPickUp() {
      try {
        this.isAnswering = true
        await this.answerCall()
      } catch (error) {
        showErrorMessage('Could not answer the call')
      } finally {
        this.isAnswering = false
      }
    },

    async onDecline() {
      try {
        await this.declineCall()
      } catch (error) {
        showErrorMessage('Could not decline the call')
      }
    },

    async onHangUp() {
      try {
        this.isHangingUp = true
        await this.hangUp()
      } catch (error) {
        showErrorMessage('Could not hang up the call')
      } finally {
        this.isHangingUp = false
      }
    },

    async onToggleMute() {
      try {
        await this.setMuted(!this.isMuted)
      } catch (error) {
        showErrorMessage('Could not change the microphone')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.softphone-widget {
  flex: 1;
}

.softphone-widget__badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.softphone-widget__badge--ringing {
  color: #fff;
  background-color: #ffc107;
}

.softphone-widget__badge--active {
  color: #fff;
  background-color: #28a745;
}

.softphone-widget__badge--error {
  color: #fff;
  background-color: #dc3545;
}

.softphone-widget__badge--offline {
  color: #fff;
  background-color: #6c757d;
}

.softphone-widget__campaign {
  font-size: 1.1rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.softphone-widget__incoming-icon {
  font-size: 2rem;
  animation: softphone-pulse 1s ease-in-out infinite;
}

.softphone-widget__incoming-label {
  font-weight: 600;
}

.softphone-widget__timer {
  font-size: 1.75rem;
  font-variant-numeric: tabular-nums;
}

.softphone-widget__caller-id {
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.softphone-widget__caller-id--masked {
  color: #adb5bd;
}

@keyframes softphone-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
